import React, { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { FilterIcon, ClearIcon, TickIcon } from '../../../bombiix-icon';
import { useForm } from 'react-hook-form';
import Popover from 'react-bootstrap/Popover';
import { FormGroup, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { NewSelectMultiSelect } from '../../PIM/common/NewSelectMultiSelect';

function BtnFilter({
  filterOnChange,
  selectedCountry,
  productComponentType,
  countriesList,
  productComponentCollection,
  onApplyFilter,
  onResetFilter,
  tagsOptions,
  selectSupplierTag,
  isFilter,
  customAttributes,
  customAttributeFilters,
}) {
  const { t } = useTranslation('common');
  const { register, handleSubmit, setValue } = useForm();

  const [show, setShow] = useState(false);

  const handleChangeCountry = (country) => {
    setValue('country', country, {
      shouldValidate: false,
    });
    filterOnChange('country', country);
  };

  const resetFilter = () => {
    setValue('country', null);
    setValue('productComponentType', []);
    setValue('selectSupplierTag', []);
    onResetFilter();
    setShow(!show);
  };

  const handleClick = () => {
    setShow(!show);
  };

  const applyFilter = (data) => {
    onApplyFilter(data);
  };

  /*
   * The selects below deliberately do NOT portal their menus to <body>. This
   * popover closes on `rootClose`, which treats any click outside the popover's
   * own DOM as a dismissal — a portalled menu lives outside it, so picking an
   * option shut the whole filter. The popover is not clipped the way a grid
   * cell is, so the menu renders correctly inside it.
   */
  return (
    <CustomTooltip placement="top" tooltipText={t('FILTER.FILTER')}>
      <button
        type="button"
        aria-label={t('FILTER.FILTER')}
        onClick={handleClick}
        className={isFilter ? 'bombiix-icon-container active' : 'bombiix-icon-container'}
      >
        <OverlayTrigger
          placement={t('BTN_FILTER.BUTTON')}
          trigger="click"
          rootClose
          overlay={
            <Popover className="custom-popover horizontal-view bombiix-filter-popover">
              <Popover.Content>
                <div className="w-100">
                  <Row className="form-row">
                    <Col md={6}>
                      <FormGroup className="common-react-select">
                        <NewSelectMultiSelect
                          label={t('FILTER.COUNTRY')}
                          name={register('country', { required: false }).name}
                          isClearable
                          showSearch
                          searchPlaceholder={t('COMMON_LABEL.SEARCH', { ns: 'common' })}
                          options={countriesList || []}
                          getOptionLabel={({ label }) => `${label}`}
                          getOptionValue={({ label }) => `${label}`}
                          value={selectedCountry}
                          onChange={handleChangeCountry}
                          placeholder={t('FILTER.SELECT_COUNTRY')}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <NewSelectMultiSelect
                          isMulti
                          label={t('FILTER.PRODUCT_TYPE_NAME')}
                          name={register('productComponentType', { required: false }).name}
                          showSearch
                          searchPlaceholder={t('COMMON_LABEL.SEARCH', { ns: 'common' })}
                          selectAllLabel={t('COMMON_LABEL.SELECT_ALL', { ns: 'common' })}
                          options={productComponentCollection}
                          value={productComponentType}
                          onChange={(val) => {
                            setValue('productComponentType', val);
                            filterOnChange('productType', val || []);
                          }}
                          placeholder={t('FILTER.SELECT_PRODUCT_TYPE')}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <NewSelectMultiSelect
                          isMulti
                          label={t('FILTER.TAG')}
                          name={register('selectSupplierTag', { required: false }).name}
                          showSearch
                          searchPlaceholder={t('COMMON_LABEL.SEARCH', { ns: 'common' })}
                          selectAllLabel={t('COMMON_LABEL.SELECT_ALL', { ns: 'common' })}
                          options={tagsOptions}
                          value={selectSupplierTag}
                          onChange={(val) => {
                            setValue('selectSupplierTag', val);
                            filterOnChange('supplierTag', val || []);
                          }}
                          placeholder={t('FILTER.SELECT_TAG')}
                        />
                      </FormGroup>
                    </Col>
                    {(customAttributes ?? [])
                      .filter((attribute) => ['dropdown', 'multi-dropdown'].includes(attribute?.type))
                      .map((attribute) => {
                        const attributeLabel = attribute.aliasName || attribute.keyName;
                        return (
                          <Col md={6} key={attribute._id}>
                            <FormGroup>
                              <NewSelectMultiSelect
                                isMulti
                                label={attributeLabel}
                                showSearch
                                searchPlaceholder={t('COMMON_LABEL.SEARCH', { ns: 'common' })}
                                selectAllLabel={t('COMMON_LABEL.SELECT_ALL', { ns: 'common' })}
                                options={(attribute?.options ?? [])
                                  .filter((option) => option?.isActive !== false)
                                  .map((option) => ({ label: option.keyName, value: option._id }))}
                                value={customAttributeFilters?.[attribute._id] ?? []}
                                onChange={(val) => filterOnChange('customAttribute', { colId: attribute._id, values: val || [] })}
                                placeholder={attributeLabel}
                              />
                            </FormGroup>
                          </Col>
                        );
                      })}
                  </Row>
                  <Row>
                    <Col md={12}>
                      <div className="d-flex justify-content-end">
                        <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.CLEAR')}>
                          <button type="button" aria-label="Clear" onClick={resetFilter} className="bombiix-icon-container">
                            <ClearIcon size={24} />
                          </button>
                        </CustomTooltip>
                        <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.APPLY')}>
                          <button type="button" aria-label="Apply" onClick={handleSubmit(applyFilter)} className="bombiix-icon-container ml-8">
                            <TickIcon size={24} />
                          </button>
                        </CustomTooltip>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Popover.Content>
            </Popover>
          }
        >
          <FilterIcon size={28} />
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
}

export default BtnFilter;
